import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Container,
  Typography,
  Grid,
  Box,
  Card,
  CardContent,
  CardMedia,
  Button,
  Chip,
  Rating,
  Stack,
  Paper,
  useTheme,
  useMediaQuery,
  Fade,
  Zoom,
  Grow,
  Dialog,
  DialogTitle,
  DialogContent,
  Divider,
  Avatar,
  IconButton,
} from '@mui/material';
import {
  Hotel,
  RoomService,
  Wifi,
  LocalParking,
  Restaurant,
  CleaningServices,
  BusinessCenter,
  MeetingRoom,
  Kitchen,
  Coffee,
  ArrowForward,
  Close,
  LocationOn,
  Star,
  Pool,
  FitnessCenter,
  Spa,
} from '@mui/icons-material';

// Only 3 types of stays: Villa, Resort, Suite
const hospitalityProperties = [
  {
    id: 1,
    title: 'Luxury Resort Villa',
    location: 'Hillside',
    image: 'https://images.unsplash.com/photo-1613977257363-707ba9348227?w=600&h=400&fit=crop',
    rating: 5.0,
    reviews: 124,
    amenities: ['WiFi', 'Restaurant', 'Room Service', 'Spa', 'Pool', 'Gym'],
    type: 'Resort',
    description: 'Experience ultimate luxury in our hillside resort villa with panoramic views.',
    beds: 4,
    baths: 5,
    sqft: 3500,
    fullDescription: 'Indulge in the epitome of luxury at our hillside resort villa. Perched on a scenic hillside, this magnificent property offers breathtaking panoramic views of the surrounding landscape. The villa features spacious living areas, a private infinity pool, a fully equipped fitness center, and a world-class spa. Every detail has been carefully curated to provide an unforgettable stay.',
    agent: {
      name: 'Rajesh Kumar',
      avatar: 'https://randomuser.me/api/portraits/men/32.jpg',
      phone: '+91 98765 43211',
      email: 'rajesh.kumar@marvelous.com',
    },
  },
  {
    id: 2,
    title: 'Executive Business Suite',
    location: 'Business Bay',
    image: 'https://images.unsplash.com/photo-1631049307264-da0ec9d70304?w=600&h=400&fit=crop',
    rating: 4.9,
    reviews: 89,
    amenities: ['WiFi', 'Conference Room', 'Business Center', 'Kitchen', 'Room Service', 'Gym'],
    type: 'Suite',
    description: 'Perfect for business travelers with all modern amenities and workspace.',
    beds: 2,
    baths: 2,
    sqft: 1500,
    fullDescription: 'Designed for the discerning business traveler, our Executive Business Suite combines comfort with functionality. Located in the heart of Business Bay, this suite offers a dedicated workspace, high-speed internet, and access to our fully equipped business center. After a long day of meetings, unwind in the comfortable living area or prepare a meal in the modern kitchen.',
    agent: {
      name: 'Priya Sharma',
      avatar: 'https://randomuser.me/api/portraits/women/44.jpg',
      phone: '+91 98765 43210',
      email: 'priya.sharma@marvelous.com',
    },
  },
  {
    id: 3,
    title: 'Private Luxury Villa',
    location: 'Hillside',
    image: 'https://images.unsplash.com/photo-1613490493576-7fde63acd811?w=600&h=400&fit=crop',
    rating: 4.8,
    reviews: 56,
    amenities: ['WiFi', 'Garden', 'Kitchen', 'Parking', 'Pool', 'Gym'],
    type: 'Villa',
    description: 'A private sanctuary with lush gardens and premium comfort.',
    beds: 3,
    baths: 3,
    sqft: 2800,
    fullDescription: 'Escape to your own private sanctuary in this stunning hillside villa. Surrounded by lush gardens and offering complete privacy, this villa is perfect for families or groups seeking a peaceful retreat. Enjoy al fresco dining on the terrace, take a dip in the private pool, or simply relax in the beautifully appointed living spaces.',
    agent: {
      name: 'Anita Desai',
      avatar: 'https://randomuser.me/api/portraits/women/68.jpg',
      phone: '+91 98765 43212',
      email: 'anita.desai@marvelous.com',
    },
  },
];

// Updated amenities
const amenities = [
  { icon: <Wifi />, label: 'High-Speed WiFi' },
  { icon: <LocalParking />, label: 'Free Parking' },
  { icon: <Restaurant />, label: 'Restaurant' },
  { icon: <CleaningServices />, label: 'Daily Cleaning' },
  { icon: <RoomService />, label: 'Room Service' },
  { icon: <Hotel />, label: 'Conference Room' },
  { icon: <BusinessCenter />, label: 'Business Center' },
  { icon: <Coffee />, label: 'Complimentary Coffee' },
];

// Amenities icons for modal
const amenityIcons = {
  Pool: <Pool />,
  Gym: <FitnessCenter />,
  Spa: <Spa />,
  WiFi: <Wifi />,
  Restaurant: <Restaurant />,
  'Room Service': <RoomService />,
  'Conference Room': <MeetingRoom />,
  'Business Center': <BusinessCenter />,
  Kitchen: <Kitchen />,
  Garden: <RoomService />,
  Parking: <LocalParking />,
};

// Section border style - matching homepage and real estate page
const sectionBorderStyle = {
  position: 'absolute',
  top: 0,
  left: 0,
  right: 0,
  height: '4px',
  background: 'linear-gradient(90deg, #9b4d3c 0%, #d49b7b 50%, #e0e0e0 100%)',
  zIndex: 1,
  boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
};

// Title style with moving underline
const titleWithUnderlineStyle = {
  fontWeight: 600,
  color: '#333',
  fontSize: { xs: '2rem', md: '2.5rem' },
  position: 'relative',
  display: 'inline-block',
  pb: 2,
  '&::after': {
    content: '""',
    position: 'absolute',
    bottom: 0,
    left: '50%',
    transform: 'translateX(-50%)',
    width: 80,
    height: 3,
    background: 'linear-gradient(90deg, #9b4d3c, #d49b7b)',
    borderRadius: 2,
    animation: 'pulseWidth 2s ease-in-out infinite',
  }
};

// Property Details Modal Component
const PropertyDetailsModal = ({ open, onClose, property }) => {
  if (!property) return null;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          border: '2px solid #9b4d3c',
          overflow: 'hidden',
        }
      }}
    >
      <DialogTitle sx={{ 
        bgcolor: '#9b4d3c', 
        color: 'white',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        py: 2,
      }}>
        <Typography variant="h5" sx={{ fontWeight: 600 }}>
          {property.title}
        </Typography>
        <IconButton onClick={onClose} sx={{ color: 'white' }}>
          <Close />
        </IconButton>
      </DialogTitle>
      
      <DialogContent sx={{ p: 0 }}>
        <Box sx={{ position: 'relative', height: 300 }}>
          <CardMedia
            component="img"
            image={property.image}
            alt={property.title}
            sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
          />
          <Chip
            label={property.type}
            sx={{
              position: 'absolute',
              top: 16,
              right: 16,
              bgcolor: property.type === 'Villa' ? '#9b4d3c' : 
                      property.type === 'Resort' ? '#d49b7b' : '#9b4d3c',
              color: 'white',
              fontWeight: 'bold',
              fontSize: '0.9rem',
              height: 30,
            }}
          />
        </Box>

        <Box sx={{ p: 3 }}>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <LocationOn sx={{ color: '#9b4d3c', mr: 1 }} />
                <Typography variant="body1" sx={{ fontWeight: 500 }}>
                  {property.location}
                </Typography>
              </Box>

              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <Star sx={{ color: '#d49b7b', mr: 1 }} />
                <Typography variant="body1" sx={{ fontWeight: 600, color: '#d49b7b', mr: 1 }}>
                  {property.rating}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  ({property.reviews} reviews)
                </Typography>
              </Box>

              <Typography variant="body1" sx={{ mb: 3, lineHeight: 1.7 }}>
                {property.fullDescription || property.description}
              </Typography>

              <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={4}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Hotel sx={{ color: '#9b4d3c' }} />
                    <Box>
                      <Typography variant="body2" color="text.secondary">Beds</Typography>
                      <Typography variant="body1" sx={{ fontWeight: 600 }}>{property.beds}</Typography>
                    </Box>
                  </Box>
                </Grid>
                <Grid item xs={4}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <RoomService sx={{ color: '#9b4d3c' }} />
                    <Box>
                      <Typography variant="body2" color="text.secondary">Baths</Typography>
                      <Typography variant="body1" sx={{ fontWeight: 600 }}>{property.baths}</Typography>
                    </Box>
                  </Box>
                </Grid>
                <Grid item xs={4}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <BusinessCenter sx={{ color: '#9b4d3c' }} />
                    <Box>
                      <Typography variant="body2" color="text.secondary">Area</Typography>
                      <Typography variant="body1" sx={{ fontWeight: 600 }}>{property.sqft} sqft</Typography>
                    </Box>
                  </Box>
                </Grid>
              </Grid>

              <Typography variant="h6" sx={{ fontWeight: 600, mb: 2, color: '#333' }}>
                Premium Amenities
              </Typography>
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 3 }}>
                {property.amenities.map((amenity, idx) => (
                  <Chip
                    key={idx}
                    icon={React.cloneElement(amenityIcons[amenity] || <Hotel />, { sx: { fontSize: 16 } })}
                    label={amenity}
                    sx={{
                      bgcolor: 'rgba(155,77,60,0.05)',
                      border: '1px solid #9b4d3c',
                      borderRadius: 2,
                      height: 36,
                      '& .MuiChip-icon': { color: '#9b4d3c' },
                    }}
                  />
                ))}
              </Box>
            </Grid>
          </Grid>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

const HospitalityPage = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [hoveredCard, setHoveredCard] = useState(null);
  const [selectedProperty, setSelectedProperty] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);

  const handleViewDetails = (property) => {
    setSelectedProperty(property);
    setModalOpen(true);
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    setSelectedProperty(null);
  };

  return (
    <Box sx={{ bgcolor: '#faf7f2', minHeight: '100vh' }}>
      {/* Property Details Modal */}
      <PropertyDetailsModal
        open={modalOpen}
        onClose={handleCloseModal}
        property={selectedProperty}
      />

      {/* SECTION 1: HEADER - With thicker gradient top border */}
      <Box sx={{ position: 'relative' }}>
        <Box sx={sectionBorderStyle} />
        <Container maxWidth="xl" sx={{ py: 4 }}>
          <Fade in={true} timeout={800}>
            <Box sx={{ textAlign: 'center' }}>
              <Typography 
                variant="overline" 
                sx={{ 
                  color: '#9b4d3c', 
                  fontSize: '0.9rem', 
                  letterSpacing: 4,
                  fontWeight: 500,
                  display: 'block',
                  mb: 1,
                }}
              >
                WELCOME TO LUXURY
              </Typography>
              <Typography 
                variant="h2" 
                component="h1" 
                sx={titleWithUnderlineStyle}
              >
                Luxury Hospitality
              </Typography>
              <Typography 
                variant="body1" 
                color="text.secondary" 
                sx={{ 
                  maxWidth: 700, 
                  mx: 'auto',
                  mt: 4,
                }}
              >
                Experience unparalleled comfort with our exclusive collection of villas, resorts, and suites
              </Typography>
            </Box>
          </Fade>
        </Container>
      </Box>

      {/* SECTION 2: AMENITIES - With thicker gradient top border */}
      <Box sx={{ position: 'relative' }}>
        <Box sx={sectionBorderStyle} />
        <Container maxWidth="xl" sx={{ py: { xs: 6, md: 8 } }}>
          <Paper 
            sx={{ 
              p: 4,
              bgcolor: 'white',
              borderRadius: 3,
              boxShadow: '0 5px 20px rgba(155,77,60,0.05)',
              border: '2px solid #d49b7b',
            }}
          >
            <Typography 
              variant="h4" 
              gutterBottom 
              align="center"
              sx={{ 
                fontWeight: 600,
                color: '#9b4d3c',
                mb: 3,
              }}
            >
              Premium Amenities
            </Typography>
            <Grid container spacing={2}>
              {amenities.map((item, index) => (
                <Grid item xs={6} sm={3} key={index}>
                  <Box 
                    sx={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      gap: 1,
                      p: 2,
                      borderRadius: 2,
                      transition: 'background-color 0.3s',
                      '&:hover': {
                        bgcolor: 'rgba(155,77,60,0.05)',
                      },
                    }}
                  >
                    <Box sx={{ color: '#9b4d3c' }}>
                      {item.icon}
                    </Box>
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                      {item.label}
                    </Typography>
                  </Box>
                </Grid>
              ))}
            </Grid>
          </Paper>
        </Container>
      </Box>

      {/* SECTION 3: AVAILABLE STAYS HEADER - CENTERED WITH MOVING UNDERLINE */}
      <Box sx={{ position: 'relative' }}>
        <Box sx={sectionBorderStyle} />
        <Container maxWidth="xl" sx={{ py: 4 }}>
          <Fade in={true} timeout={800}>
            <Box sx={{ textAlign: 'center', mb: 2 }}>
              <Typography 
                variant="h2" 
                component="h2" 
                sx={titleWithUnderlineStyle}
              >
                Available Stays
              </Typography>
            </Box>
          </Fade>
        </Container>
      </Box>

      {/* SECTION 4: PROPERTIES GRID - MATCHING REAL ESTATE CARD SIZE AND STYLE */}
      <Container maxWidth="xl" sx={{ py: { xs: 6, md: 8 } }}>
        <Grid container spacing={4}>
          {hospitalityProperties.map((property, index) => (
            <Grid item xs={12} md={4} key={property.id}>
              <Grow in={true} timeout={500 + index * 100}>
                <Card 
                  onMouseEnter={() => setHoveredCard(property.id)}
                  onMouseLeave={() => setHoveredCard(null)}
                  sx={{ 
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                    borderRadius: 3,
                    overflow: 'hidden',
                    boxShadow: hoveredCard === property.id 
                      ? '0 25px 50px rgba(155,77,60,0.25)' 
                      : '0 5px 20px rgba(0,0,0,0.05)',
                    border: '2px solid #9b4d3c',
                    transform: hoveredCard === property.id ? 'translateY(-8px)' : 'translateY(0)',
                    '&:hover': {
                      borderColor: '#d49b7b',
                    },
                  }}
                >
                  <Box sx={{ position: 'relative', overflow: 'hidden' }}>
                    <CardMedia
                      component="img"
                      height="220"
                      image={property.image}
                      alt={property.title}
                      sx={{
                        transition: 'transform 0.5s cubic-bezier(0.4, 0, 0.2, 1)',
                        transform: hoveredCard === property.id ? 'scale(1.08)' : 'scale(1)',
                      }}
                    />
                    {property.rating === 5.0 && (
                      <Zoom in={true} timeout={500}>
                        <Chip
                          label="PREMIUM"
                          size="small"
                          sx={{
                            position: 'absolute',
                            top: 15,
                            left: 15,
                            bgcolor: '#9b4d3c',
                            color: 'white',
                            fontWeight: 600,
                            fontSize: '0.7rem',
                            height: 24,
                          }}
                        />
                      </Zoom>
                    )}
                  </Box>

                  <CardContent sx={{ flex: 1, p: 2.5 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
                      <Typography variant="h6" sx={{ fontWeight: 600, fontSize: '1.1rem', color: '#333' }}>
                        {property.title}
                      </Typography>
                      <Chip 
                        label={property.type} 
                        size="small" 
                        sx={{ 
                          bgcolor: property.type === 'Villa' ? '#9b4d3c' : 
                                  property.type === 'Resort' ? '#d49b7b' : '#9b4d3c',
                          color: 'white',
                          fontWeight: 600,
                          fontSize: '0.7rem',
                          height: 22,
                        }} 
                      />
                    </Box>
                    
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
                      <LocationOn sx={{ fontSize: 16, color: '#9b4d3c', mr: 0.5 }} />
                      <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                        {property.location}
                      </Typography>
                    </Box>
                    
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5, lineHeight: 1.6 }}>
                      {property.description}
                    </Typography>
                    
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                      <Star sx={{ fontSize: 16, color: '#d49b7b', mr: 0.5 }} />
                      <Typography variant="body2" sx={{ fontWeight: 600, color: '#d49b7b', mr: 1 }}>
                        {property.rating}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        ({property.reviews} reviews)
                      </Typography>
                    </Box>

                    <Button
                      fullWidth
                      variant="contained"
                      onClick={() => handleViewDetails(property)}
                      sx={{
                        mt: 1,
                        py: 1,
                        borderRadius: 2,
                        fontWeight: 600,
                        fontSize: '0.9rem',
                        bgcolor: '#9b4d3c',
                        color: 'white',
                        transition: 'all 0.3s ease',
                        '&:hover': {
                          bgcolor: '#7a3b2e',
                          transform: 'translateY(-2px)',
                          boxShadow: '0 5px 15px rgba(155,77,60,0.3)',
                        },
                      }}
                      endIcon={<ArrowForward />}
                    >
                      View Details
                    </Button>
                  </CardContent>
                </Card>
              </Grow>
            </Grid>
          ))}
        </Grid>
      </Container>

      {/* SECTION 5: CORPORATE SERVICES - With thicker gradient top border */}
      <Box sx={{ position: 'relative' }}>
        <Box sx={sectionBorderStyle} />
        <Container maxWidth="xl" sx={{ py: { xs: 6, md: 8 } }}>
          <Grid container spacing={6} alignItems="center">
            <Grid item xs={12} md={6}>
              <Box>
                <Typography 
                  variant="overline" 
                  sx={{ 
                    color: '#9b4d3c', 
                    fontSize: '0.9rem', 
                    letterSpacing: 4,
                    fontWeight: 500,
                    mb: 2,
                    display: 'block',
                  }}
                >
                  FOR BUSINESS
                </Typography>
                <Typography 
                  variant="h3" 
                  gutterBottom 
                  sx={{ 
                    fontWeight: 600,
                    color: '#333',
                    fontSize: { xs: '2rem', md: '2.5rem' },
                  }}
                >
                  Corporate Housing Solutions
                </Typography>
                <Typography 
                  variant="body1" 
                  paragraph 
                  sx={{ 
                    fontSize: '1rem', 
                    lineHeight: 1.7,
                    color: '#555',
                    mb: 4,
                  }}
                >
                  We provide customized corporate accommodations tailored to your business needs. 
                  From fully-furnished suites to personalized amenities, we ensure your employees 
                  feel at home during their extended stays.
                </Typography>
                
                <Grid container spacing={2} sx={{ mb: 4 }}>
                  {[
                    { icon: <BusinessCenter />, text: 'Customized billing' },
                    { icon: <MeetingRoom />, text: 'Flexible check-in/out' },
                    { icon: <Kitchen />, text: 'Fully equipped kitchens' },
                    { icon: <Wifi />, text: 'High-speed internet' },
                  ].map((item, idx) => (
                    <Grid item xs={6} key={idx}>
                      <Box 
                        sx={{ 
                          display: 'flex', 
                          alignItems: 'center', 
                          gap: 1,
                          p: 1,
                          borderRadius: 2,
                          '&:hover': {
                            bgcolor: 'rgba(155,77,60,0.05)',
                          },
                        }}
                      >
                        <Box sx={{ color: '#9b4d3c' }}>{item.icon}</Box>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>{item.text}</Typography>
                      </Box>
                    </Grid>
                  ))}
                </Grid>

                <Button 
                  component={Link}
                  to="/contact"
                  variant="contained" 
                  size="large"
                  endIcon={<ArrowForward />}
                  sx={{ 
                    bgcolor: '#9b4d3c',
                    px: 4,
                    py: 1.5,
                    fontSize: '1rem',
                    fontWeight: 600,
                    borderRadius: 2,
                    '&:hover': { 
                      bgcolor: '#7a3b2e',
                    },
                  }}
                >
                  Corporate Inquiry
                </Button>
              </Box>
            </Grid>
            <Grid item xs={12} md={6}>
              <Zoom in={true} timeout={1000}>
                <Paper
                  sx={{
                    position: 'relative',
                    borderRadius: 3,
                    overflow: 'hidden',
                    boxShadow: '0 5px 20px rgba(155,77,60,0.1)',
                    border: '2px solid #9b4d3c',
                    transition: 'transform 0.3s, box-shadow 0.3s',
                    '&:hover': {
                      transform: 'scale(1.02)',
                      boxShadow: '0 15px 30px rgba(155,77,60,0.2)',
                    },
                  }}
                >
                  <Box
                    component="img"
                    src="https://images.unsplash.com/photo-1522708323590-d24dbb6b0267?w=600&h=400&fit=crop"
                    alt="Corporate Housing"
                    sx={{ 
                      width: '100%', 
                      height: 'auto',
                      display: 'block',
                    }}
                  />
                  <Box
                    sx={{
                      position: 'absolute',
                      bottom: 0,
                      left: 0,
                      right: 0,
                      p: 3,
                      background: 'linear-gradient(135deg, rgba(155,77,60,0.9) 0%, rgba(212,155,123,0.9) 100%)',
                      color: 'white',
                    }}
                  >
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                      Modern Corporate Suites
                    </Typography>
                    <Typography variant="body2" sx={{ opacity: 0.9 }}>
                      Fully furnished • Ready to move in
                    </Typography>
                  </Box>
                </Paper>
              </Zoom>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* SECTION 6: FOOTER - With thicker gradient top border */}
      <Box sx={{ position: 'relative' }}>
        <Box sx={sectionBorderStyle} />
        <Container maxWidth="xl" sx={{ py: 4 }}>
          <Box sx={{ textAlign: 'center' }}>
            <Typography variant="body2" color="text.secondary">
              Experience the Marvellous difference
            </Typography>
          </Box>
        </Container>
      </Box>

      {/* Add keyframe animation for the moving underline */}
      <style>
        {`
          @keyframes pulseWidth {
            0%, 100% { width: 80px; }
            50% { width: 120px; }
          }
        `}
      </style>
    </Box>
  );
};

export default HospitalityPage;